import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link, useLocation } from "react-router";

import {
  RobotoDivider,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks/useCheckFeatureFlag";

import styles from "./DeviceTabs.module.css";
import { useActiveDeviceTab, DeviceTab } from "./useActiveDeviceTabFromUrl";

/**
 * Tab strip of the device page. Reads its active value from the URL via
 * `useActiveDeviceTab`, so it carries no device-record dependency and renders
 * before the device has loaded.
 */
export function DeviceTabs() {
  // Derived from the URL each tab's <Link> navigates to, so there is no local
  // tab state that could drift from the address bar.
  const activeTab = useActiveDeviceTab();
  const location = useLocation();
  // Show the Sessions tab only when `release-sessions` is on.
  // The same flag gates the /devices/:deviceId/sessions route in DevicesRoutes.tsx, so
  // hiding the tab here keeps the strip aligned with reachable routes.
  const sessionsTabEnabled = useCheckFeatureFlag("release-sessions");
  // Same flag gates the /devices/:deviceId/metrics route in DevicesRoutes.tsx.
  const metricsTabEnabled = useCheckFeatureFlag("release-metrics");
  // Same flag gates the /devices/:deviceId/configurations route in DevicesRoutes.tsx.
  const configurationsTabEnabled = useCheckFeatureFlag("release-asset-store");

  return (
    <div className={styles.tabBar}>
      <RobotoStyledTabs value={activeTab} aria-label="device tabs">
        {sessionsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Sessions}
            label="Sessions"
            icon={<RouteOutlinedIcon className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={{ pathname: DeviceTab.Sessions, search: location.search }}
            replace
          />
        )}
        <RobotoStyledTab
          value={DeviceTab.Datasets}
          label="Datasets"
          icon={<DatasetOutlinedIcon className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={{ pathname: DeviceTab.Datasets, search: location.search }}
          replace
        />
        {configurationsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Configurations}
            label="Configurations"
            icon={<SettingsOutlinedIcon className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={{ pathname: DeviceTab.Configurations, search: location.search }}
            replace
          />
        )}
        <RobotoStyledTab
          value={DeviceTab.Files}
          label="Files"
          icon={<FolderOutlinedIcon className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={{ pathname: DeviceTab.Files, search: location.search }}
          replace
        />
        {metricsTabEnabled && (
          <RobotoStyledTab
            value={DeviceTab.Metrics}
            label="Metrics"
            icon={<BarChartOutlinedIcon className={styles.tabIcon} />}
            iconPosition="start"
            component={Link}
            to={{ pathname: DeviceTab.Metrics, search: location.search }}
            replace
          />
        )}
        <RobotoStyledTab
          value={DeviceTab.Details}
          label="Details"
          icon={<InfoOutlinedIcon className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={{ pathname: DeviceTab.Details, search: location.search }}
          replace
        />
        <RobotoStyledTab
          value={DeviceTab.Tokens}
          label="Tokens"
          icon={<KeyOutlinedIcon className={styles.tabIcon} />}
          iconPosition="start"
          component={Link}
          to={{ pathname: DeviceTab.Tokens, search: location.search }}
          replace
        />
      </RobotoStyledTabs>
      <RobotoDivider />
    </div>
  );
}
